import { useState, useEffect, useCallback } from 'react'
import { useToast } from '@winnin/winnin_ui.components.toast'

import LinkToast from 'components/Toasts/LinkToast'
import { useTerritoriesFavoriteTotal } from 'hooks/useTerritoriesFavoriteTotal'
import { useTerritoryMutations } from 'hooks-queries/territory'
import { useTranslate } from 'lib/tolgee'
import { useIsGlobalTaxonomy } from 'modules/global-topic/common/global-topic/hooks/use-is-global-taxonomy'
import { PanelStore, UserStore } from 'stores'
import { analyticsTracker } from 'utils'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import { GET_FAVORITE_TERRITORIES_MUTATION_LOG, GET_TERRITORIES_FAVORITE_TOTAL_LOG } from './favorite.consts'
import { translations } from './favorite.i18n'
import type { IFavoriteProps, IFavoriteModel } from './favorite.types'

export const useFavoriteModel = ({
  initialSaveState,
  territoryId,
  territoryType,
  territoryName,
}: IFavoriteProps): IFavoriteModel => {
  const [isSaved, setIsSaved] = useState(initialSaveState)
  const t = useTranslate()
  const { createToast } = useToast()
  const workspaceId = Object(PanelStore?.selectedPanel)?.panelId

  const isViewerUser = UserStore.getRole === 'viewer'
  const { isGlobalTaxonomyWorkspace } = useIsGlobalTaxonomy()
  const topicsRoute = getRoute(PATHNAMES.WORKSPACE_TOPICS, {
    panelId: workspaceId,
    topicsType: 'territories',
  })
  const favoritesRoute = getRoute(PATHNAMES.WORKSPACE_TOPICS, {
    panelId: workspaceId,
    topicsType: 'favorites',
  })

  const { territoriesFavoriteTotal, loading } = useTerritoriesFavoriteTotal({
    errorLog: GET_TERRITORIES_FAVORITE_TOTAL_LOG,
  })

  const [setFavoriteTerritory, { loading: favoriteMutationLoading }] = useTerritoryMutations({
    mutationName: 'favoriteTerritory',
    errorLog: GET_FAVORITE_TERRITORIES_MUTATION_LOG,
  })

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e?.stopPropagation()
      e?.preventDefault()
      const stateToGoBack = isSaved

      setFavoriteTerritory({
        variables: {
          territoriesToFollow: !isSaved ? [territoryId] : [],
          territoriesToUnFollow: isSaved ? [territoryId] : [],
        },
        update: cache => {
          const cacheId = `Workspace:${workspaceId}`
          cache.evict({ id: cacheId, fieldName: 'followedTerritories' })

          const territoryCacheId = `Territory:${territoryId}`
          cache.modify({
            id: territoryCacheId,
            fields: {
              isFollowed() {
                return !isSaved
              },
            },
          })
        },
        onCompleted: data => {
          if (data?.editManageWorkspaceFollowTerritory?.success !== true) {
            setIsSaved(stateToGoBack)
            return
          }

          if (!isSaved) {
            const link = isGlobalTaxonomyWorkspace ? favoritesRoute : `${topicsRoute}?territoryTypes=favorites`
            const toast = {
              message: t(translations.successToast),
              link,
            }

            createToast({
              content: <LinkToast toast={toast} inline={false} />,
            })
            return
          }

          createToast({
            content: t(translations.removedToast),
          })
        },
        onError: () => {
          setIsSaved(stateToGoBack)
        },
      })

      setIsSaved(!isSaved)

      if (!isSaved) {
        analyticsTracker.eventTrack('Favorite topic', {
          'Topic name': territoryName,
          'Topic type': territoryType,
        })
      }
    },
    [
      isSaved,
      setFavoriteTerritory,
      territoryId,
      workspaceId,
      createToast,
      t,
      isGlobalTaxonomyWorkspace,
      favoritesRoute,
      topicsRoute,
      territoryName,
      territoryType,
    ]
  )

  const cantSave = Number.isInteger(territoriesFavoriteTotal) && territoriesFavoriteTotal >= 20 && !isSaved

  useEffect(() => {
    setIsSaved(initialSaveState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId])

  return {
    isSaved,
    cantSave,
    isViewerUser,
    handleButtonClick,
    loading: loading || favoriteMutationLoading,
  }
}
